import React, { useMemo } from 'react';
import { BwjRadioButtonSet, RadioButtonSetOrientation } from '../../../../framework/components/bwj-radio-button-set';
import { StormDoorColor } from '../../../../entities-generated/common/storm-door-color';
import { DoorSortMode } from '../../entities/door-sort-options';
import { IStormDoorColorOption } from '../../../../entities-generated/common/storm-door-color-option';
import { IBwjFormOption } from '../../../../framework/entities/bwj-form-option';
import { BwjInput } from '../../../../framework/entities/bwj-input';
import { firstOrDefault } from '../../../../framework/utils/collection';
import { CustomerAccountService } from '../../../../services/customer-account';
import { IStyle, mergeStyleSets } from '@fluentui/react';
import { BwjButton, BwjButtonType } from '../../../../framework/components/bwj-button';
import { StormDoorColorSelector } from '../../../components/st-door-color-select';

interface IStormShoppingPrefsSelectorProps {
  selectedColors: StormDoorColor[];
  sortMode: DoorSortMode;
  onSortModeChanged: (option: DoorSortMode) => void;
  onColorPrefChangeRequested: () => void;
}

const getStyleClasses = () => {
  const root: IStyle = {
    padding: '0 1.5rem',
  };

  const prefContainer: IStyle = {
    marginBottom: '2rem',

    '& button': {
      marginTop: '1.2rem',
    },
  };

  const heading: IStyle = {
    margin: '1.2rem 0',
  };

  return mergeStyleSets({
    root,
    prefContainer,
    heading,
  });
};

const sortOptions: IBwjFormOption<DoorSortMode>[] = [
  {
    value: DoorSortMode.Price,
    description: 'Lowest price',
  }, {
    value: DoorSortMode.Quality,
    description: 'Best quality',
  }, {
    value: DoorSortMode.Rating,
    description: 'Highest rated',
  }, {
    value: DoorSortMode.Bestseller,
    description: 'Most purchased',
  },
];

export const StormShoppingPrefsSelector: React.FunctionComponent<IStormShoppingPrefsSelectorProps> = (props) => {
  const { selectedColors, sortMode } = props;
  const cs = getStyleClasses();
  const sortBy = useMemo(() => {
    const option = firstOrDefault(sortOptions, o => o.value === sortMode)!;
    return new BwjInput<IBwjFormOption<DoorSortMode>>(option);
  }, [sortMode]);
  const colors = useMemo(() => {
    const allColors = CustomerAccountService.projectConfiguration?.stormDoorColorOptions;
    if(!allColors) { return []; } // should never happen

    const result: IStormDoorColorOption[] = [];
    selectedColors.map(sc => {
      const option = firstOrDefault(allColors, c => c.id === sc);
      !!option && result.push(option);
      return option;
    });
    return result;
  }, [selectedColors]);

  const onSortChange = (option: IBwjFormOption<DoorSortMode> | null) => {
    if(!option) { return; }
    props.onSortModeChanged(option.value);
  }

  return (
    <div className={cs.root}>
      <div className={cs.prefContainer}>
        <h3 className={cs.heading}>
          Color Families
        </h3>
        <StormDoorColorSelector
          swatchSize={3}
          options={colors}
          selected={[]}
          showLabels={true}
          rootStyle={{}}
        />
        <BwjButton
          buttonStyle={BwjButtonType.PrimaryStandard}
          text={'Change colors'}
          onClick={props.onColorPrefChangeRequested}
        />
      </div>
      <div className={cs.prefContainer}>
        <h3 className={cs.heading}>
          Sort Doors By
        </h3>
        <BwjRadioButtonSet<IBwjFormOption<DoorSortMode>>
          id={'rbtnSort'}
          required={false}
          onUpdate={onSortChange}
          input={sortBy}
          options={sortOptions}
          orientation={RadioButtonSetOrientation.Vertical}
          optionDisplayKey={'description'}
          optionValueKey={'value'}
        />
      </div>
    </div>
  );
};
